import { NavLink, Link } from "react-router-dom";
import { Film, Newspaper, Menu, X, Mail, FileText, Instagram, Cookie } from "lucide-react";
import { useState } from "react";
import { InstallPWAButton } from "./InstallPWAButton";
import { openCookieSettings } from "./CookieConsent";
import { NewsletterBand } from "./NewsletterSignup";

const nav = [
  { to: "/", label: "Accueil", end: true },
  { to: "/sorties", label: "Sorties" },
  { to: "/deja-dans-les-salles", label: "Déjà dans les salles" },
  { to: "/deja-sur-vos-ecrans", label: "Déjà sur vos écrans" },
  { to: "/top", label: "Préférés" },
  { to: "/articles", label: "Articles" },
];

export const PublicNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-[var(--line)]">
      {/* Brand logo area — logo + baseline (header only). */}
      <Link
        to="/"
        data-testid="brand-logo-slot"
        aria-label="Rouge Première — Accueil"
        className="flex flex-col items-center pt-2 pb-7 md:pt-3 md:pb-12"
      >
        <img
          src="/logo-final-htedef.png"
          alt="Rouge Première"
          className="h-72 md:h-[28rem] w-auto max-w-[96%] select-none"
          draggable={false}
        />
        <span
          data-testid="header-baseline"
          className="-mt-16 md:-mt-28 px-3 md:px-0 whitespace-nowrap max-w-full text-[9px] min-[380px]:text-[10px] md:text-[11px] uppercase tracking-[0.32em] pl-[calc(0.75rem+0.32em)] min-[380px]:tracking-[0.42em] min-[380px]:pl-[calc(0.75rem+0.42em)] md:pl-0 md:tracking-[1.45em] text-neutral-600"
        >
          Sorties en séries et en salle
        </span>
      </Link>
      <span data-testid="pub-logo" className="sr-only">Rouge Première</span>

      {/* Navigation strip */}
      <div className="border-t border-[var(--line)]">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 h-12 md:h-14 flex items-center justify-between">
          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`pub-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.25em] font-medium transition-colors ${
                    isActive ? "text-[var(--red)]" : "text-neutral-700 hover:text-black"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/admin"
            data-testid="pub-nav-admin"
            className="hidden md:inline-flex text-[11px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]"
          >
            Espace rédaction
          </Link>

          <button
            className="md:hidden p-2 -ml-2"
            onClick={() => setOpen(!open)}
            aria-label="menu"
            data-testid="pub-nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <span className="md:hidden text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            L'agenda cinéma & séries
          </span>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--line)] bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] ${isActive ? "text-[var(--red)]" : "text-neutral-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/admin" onClick={() => setOpen(false)} className="text-sm text-neutral-500">
              Espace rédaction
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const PublicFooter = () => (
  <footer className="mt-24 border-t border-[var(--line)] bg-[var(--bg-cream)]">
    <NewsletterBand />
    <div className="max-w-[1300px] mx-auto px-5 md:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <img
          data-testid="footer-logo-slot"
          src="/logo-final-htedef.png"
          alt="Rouge Première"
          className="h-28 md:h-36 w-auto mb-4 select-none"
          draggable={false}
        />
        <p className="text-sm text-neutral-600 max-w-sm leading-relaxed">
          Le rendez-vous des sorties cinéma et séries. Éditorial, agenda, coups de cœur —
          le rideau se lève chaque jour.
        </p>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
          Explorer
        </div>
        <ul className="space-y-2 text-sm text-neutral-700">
          <li><Link to="/sorties" className="hover:text-[var(--red)]"><Film className="inline h-3.5 w-3.5 mr-1.5" />Sorties</Link></li>
          <li><Link to="/articles" className="hover:text-[var(--red)]"><Newspaper className="inline h-3.5 w-3.5 mr-1.5" />Articles</Link></li>
          <li><Link to="/newsletter" data-testid="footer-newsletter-link" className="hover:text-[var(--red)]"><Mail className="inline h-3.5 w-3.5 mr-1.5" />Newsletter</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
          Informations
        </div>
        <ul className="space-y-2 text-sm text-neutral-700">
          <li>
            <Link to="/contact" data-testid="footer-contact-link" className="hover:text-[var(--red)]">
              <Mail className="inline h-3.5 w-3.5 mr-1.5" />Nous écrire
            </Link>
          </li>
          <li>
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="hover:text-[var(--red)]">
              <FileText className="inline h-3.5 w-3.5 mr-1.5" />Mentions légales
            </Link>
          </li>
          <li>
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
              className="hover:text-[var(--red)] transition-colors"
            >
              <Cookie className="inline h-3.5 w-3.5 mr-1.5" />Gérer les cookies
            </button>
          </li>
          <li>
            <a
              href="https://www.instagram.com/rougepremiere/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Rouge Première sur Instagram"
              className="hover:text-[var(--red)]"
            >
              <Instagram className="inline h-3.5 w-3.5 mr-1.5" />Instagram
            </a>
          </li>
          <li>
            <InstallPWAButton />
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[var(--line)]">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 py-5 text-[11px] text-neutral-500 tracking-widest uppercase flex items-center justify-between">
        <span>© {new Date().getFullYear()} ROUGEPREMIERE</span>
        <span>Made with soin</span>
      </div>
    </div>
    <div className="border-t border-[var(--line)]" data-testid="footer-tmdb-attribution">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 py-4 flex flex-col sm:flex-row sm:items-center gap-3 text-[11px] text-neutral-500 leading-relaxed">
        <a href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer" aria-label="The Movie Database (TMDB)" className="shrink-0">
          <img src="/tmdb-logo.svg" alt="TMDB" className="h-3 w-auto" />
        </a>
        <span>
          Ce site utilise l'API TMDB mais n'est ni approuvé ni certifié par TMDB. Affiches, synopsis et
          informations sur les films et séries : © TMDB et leurs ayants droit.
        </span>
      </div>
    </div>
  </footer>
);
